import { router, usePage } from "@inertiajs/react"
import { useLocalStorage, useSessionStorage } from "usehooks-ts"
import { PuzzleAnalytics, UsePuzzleAnalyticsProps } from "@/types/types"
import { captureEvent } from "@/lib/posthog"

const isValidAnalytics = (data: any): data is PuzzleAnalytics =>
  typeof data?.attempts === "number" &&
  typeof data?.complete_first_try === "number"

const usePuzzleAnalytics = () => {
  const { puzzle } = usePage<UsePuzzleAnalyticsProps>().props
  const { id: puzzleId, analytics } = puzzle || {}

  const [completedPuzzles, setCompletedPuzzles] = useLocalStorage<string[]>(
    "puzzles-completed",
    []
  )

  // Mistakes are stored in sessionStorage instead of localStorage
  const [puzzleMistakes, setPuzzleMistakes] = useSessionStorage<string[]>(
    "puzzle-mistakes",
    []
  )

  const hasCompletedPuzzle = completedPuzzles.some((id) => id === puzzleId)
  const hasMadeMistake = puzzleMistakes.some((id) => id === puzzleId)

  const logPuzzleStarted = () => {
    if (!puzzleId) return
    if (hasMadeMistake) return

    captureEvent("puzzle_attempted", { puzzle_id: puzzleId })

    router.post(
      `/puzzles/${puzzleId}/events`,
      {
        event_name: "start",
      },
      {
        async: true,
        preserveScroll: true,
        preserveState: true,
      }
    )
  }

  const logPuzzleMistake = () => {
    if (!puzzleId) return
    if (hasMadeMistake) return

    setPuzzleMistakes((prev) => [...prev, puzzleId])
  }

  const logPuzzleCompleted = () => {
    if (!puzzleId) return

    // If puzzle has been completed before, don't save
    if (hasCompletedPuzzle) return

    setCompletedPuzzles([...completedPuzzles, puzzleId])
    captureEvent("puzzle_completed", { puzzle_id: puzzleId, first_try: !hasMadeMistake })

    const event = hasMadeMistake ? "complete" : "complete_first_try"

    router.post(
      `/puzzles/${puzzleId}/events`,
      {
        event_name: event,
      },
      {
        async: true,
        preserveScroll: true,
        preserveState: true,
      }
    )
  }

  const percentageFirstTry =
    isValidAnalytics(analytics) &&
    analytics.complete_first_try <= analytics.attempts
      ? Math.round((analytics.complete_first_try / analytics.attempts) * 100)
      : undefined

  return {
    logPuzzleStarted,
    logPuzzleMistake,
    logPuzzleCompleted,
    percentageFirstTry,
  }
}

export default usePuzzleAnalytics
